import type {
  CSSProperties,
  ComponentPropsWithRef,
  MouseEventHandler,
} from "react";
import { forwardRef, useCallback } from "react";
import { Column, Link, Row, Section } from "@react-email/components";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { useParseStyles } from "@circle-react-shared/Emails/VisualEmailBuilder/Design/useParseStyles";
import type { BulletProp } from "./Bullet";
import { Bullet } from "./Bullet";
import { defaultStyles } from "./defaultStyles";

const dot = "●";

export interface ListItem {
  bullet?: BulletProp;
  bulletStyles?: Record<string, StyleValue>;
  id: string;
  url?: string;
  value: string;
}

interface ListElementProps extends ComponentPropsWithRef<"table"> {
  "data-preview-mode": boolean;
  items: ListItem[];
  styles?: Record<string, StyleValue>;
}

const hasCustomBullet = (bullet?: BulletProp): bullet is BulletProp =>
  Boolean(bullet?.emoji || bullet?.signed_id || bullet?.placeholder);

export const ListBlock = forwardRef<HTMLTableElement, ListElementProps>(
  (
    { items = [], styles = {}, "data-preview-mode": isPreviewMode, ...rest },
    ref,
  ) => {
    const inlineStyles = useParseStyles({
      styles,
      defaultStyles,
    }) as CSSProperties;

    const handleClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
      event => {
        if (isPreviewMode) {
          event.preventDefault();
        }
      },
      [isPreviewMode],
    );

    return (
      <Section ref={ref} style={inlineStyles} {...rest}>
        {items.map(({ id, bullet, value, url, bulletStyles = {} }) => (
          <Row key={id}>
            <Column
              style={{
                verticalAlign: "middle",
                paddingRight: "8px",
                paddingBottom: "8px",
                width: hasCustomBullet(bullet) ? "18px" : "1em",
                textAlign: "center",
              }}
            >
              {hasCustomBullet(bullet) ? (
                <Bullet bullet={bullet} styles={bulletStyles} />
              ) : (
                <span>{dot}</span>
              )}
            </Column>
            <Column
              style={{
                verticalAlign: "middle",
                paddingBottom: "8px",
              }}
            >
              {url ? (
                <Link
                  href={url}
                  style={{
                    color: "inherit",
                    textDecoration: "underline",
                  }}
                  onClick={handleClick}
                >
                  {value}
                </Link>
              ) : (
                <span>{value}</span>
              )}
            </Column>
          </Row>
        ))}
      </Section>
    );
  },
);

ListBlock.displayName = "ListBlock";
